import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  defaultExam,
  defaultExamInput,
  Exam,
  ExamInput,
  Question,
  Result,
} from './type';
import {
  addOneToExamTimes,
  addQuestion,
  createNewExam,
  fetchExamById,
  fetchResultByExamId,
  fetchResultByStudentIdAndExamId,
  updateExamById,
  updateQuestionById,
} from '../../api';
import AnswerResultPage from './AnswerResultPage';
import { useDispatch } from 'react-redux';
import {
  addCourseItem,
  updateCourseItem,
} from '../../redux/slices/courseItemSlice';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CloseIcon from '@mui/icons-material/Close';
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
import LeaderBoardPage from './LeaderBoardPage';
import { sortResults } from './extractQuestions';
import { Download } from '@mui/icons-material';
import CountDownToResult from './CountDownToResult';
import { useAppSelector } from '../../hooks/redux';

interface ExamTabsProps {
  examId?: string;
  mode?: 'view' | 'edit' | 'create';
  courseId?: string;
  parentFolderId?: string;
  closeDialog?: () => void;
  isTeacher?: boolean;
  examTemplate?: Exam;
}

const fullSteps = [
  {
    label: 'Đề thi',
    icon: <AssignmentIcon />,
  },
  {
    label: 'Bảng xếp hạng',
    icon: <LeaderboardIcon />,
  },
];

const ExamTabs: React.FC<ExamTabsProps> = ({
  examId,
  mode = 'view',
  courseId = '',
  parentFolderId = '',
  closeDialog = () => {},
  isTeacher = false,
  examTemplate,
}) => {
  const { examId: urlExamId } = useParams<{ examId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [exam, setExam] = useState<Exam | null>(defaultExam);
  const [examInput, setExamInput] = useState(defaultExamInput);
  const { user } = useAuth();
  const [isDataFilled, setIsDataFilled] = useState<boolean>(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<Result | null>(null);
  const isMobile = useResponsive('down', 'md');
  const [results, setResults] = useState<Result[]>([]);
  const [isDataAllFetch, setIsDataAllFetch] = useState(false);
  const [isExamDataFetch, setIsExamDataFetch] = useState(false);
  const [isResultDataFetch, setIsResultDataFetch] = useState(false);
  const [showResult, setShowResult] = React.useState(false);
  const timediff = useAppSelector((state) => state.app.timediff);
  const sortedResults = sortResults(results, exam);

  console.log('ExamTabs examInput:', examInput);

  useEffect(() => {
    if (exam?.examId) {
      fetchResultByExamId(exam.examId).then((data) => {
        if (data) {
          setResults(data);
        }
      });
    }
  }, [exam?.examId]);

  useEffect(() => {
    if (examTemplate && examTemplate.examId) {
      setExam(examTemplate);

      setExamInput({
        title: examTemplate.title || '',
        duration: examTemplate.duration || '',
        subject: examTemplate.subject || '',
        executedAt: examTemplate.executedAt
          ? new Date(examTemplate.executedAt)
          : null,
        questions: examTemplate.questions || [],
        isExercise: examTemplate.isExercise || false,
        onlyForMembers: examTemplate.onlyForMembers || false,
        listLinks: examTemplate.listLinks || [],
        pdfLink: examTemplate.pdfLink || '',
      } as ExamInput);
    }
  }, [examTemplate]);

  useEffect(() => {
    setIsDataFilled(
      !(
        examInput.title &&
        examInput.duration &&
        examInput.subject &&
        examInput.questions.length > 0
      )
    );
  }, [examInput]);

  //Remove the text after the last slash
  const path = location.pathname.substring(
    0,
    location.pathname.lastIndexOf('/')
  );

  const isNotOnDialog = path.includes('answer') || path.includes('leaderboard');

  // const height = isNotOnDialog ? '90vh' : '80vh';

  // Fetch exam data based on the examId
  useEffect(() => {
    const loadExam = async () => {
      try {
        const id = examId || urlExamId;
        if (!id) return;
        if (!exam || exam.examId !== id) {
          const res = await fetchExamById(id);

          const data = res.data;
          setExam(data);
          setIsExamDataFetch(true);
          setIsDataAllFetch(isResultDataFetch);
          setExamInput({
            title: data.title || '',
            duration: data.duration || '',
            subject: data.subject || '',
            executedAt: data.executedAt ? new Date(data.executedAt) : null,
            questions: data.questions || [],
            isExercise: data.isExercise || false,
            onlyForMembers: data.onlyForMembers || false,
            listLinks: data.listLinks || [],
            pdfLink: data.pdfLink || '',
            ...data,
          } as ExamInput);

          await addOneToExamTimes(id);
        }
      } catch (err) {
        console.error('Error fetching exam:', err);
      }
    };

    loadExam();
  }, [examId, urlExamId, isResultDataFetch, exam]);

  //Fetch result data from api based on studentId and examId
  //fetchResultByStudentIdAndExamId
  useEffect(() => {
    const loadResult = async () => {
      try {
        if (!exam) return;
        if (!user || !user._id) {
          setIsResultDataFetch(true);
          setIsDataAllFetch(isExamDataFetch);
          return;
        }
        const res = await fetchResultByStudentIdAndExamId(
          user._id,
          exam.examId
        );
        const data = res.data;
        if (data) {
          //Add field check to each answer
          data.answers = data.answers.map((answer: any) => ({
            ...answer,
            check: true,
          }));
          setResult(data);
        }
        setIsResultDataFetch(true);
        setIsDataAllFetch(isExamDataFetch);
      } catch (err) {
        console.error('Error fetching result:', err);
      }
    };

    loadResult();
  }, [exam, user, isExamDataFetch]);

  // Determine active tab based on URL
  useEffect(() => {
    if (location.pathname.includes('leaderboard')) {
      setActiveTab(1);
    } else if (location.pathname.includes('answer')) {
      setActiveTab(0);
    }
  }, [location.pathname]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const id = examId || urlExamId;
    if (isNotOnDialog) {
      switch (newValue) {
        case 0:
          navigate(`/answer/${id}`);
          break;
        case 1:
          navigate(`/leaderboard/${id}`);
          break;
        default:
          break;
      }
    }
  };

  const handleSave = async () => {
    setLoadingButton(true);
    setProgress(0); // Reset progress to 0 at the start

    const totalQuestions = examInput.questions.length;

    

    const processQuestionData = (question: Question, examId: string) => {
      const questionData = new FormData();

      let correctAnswer = question.correctAnswer;
      if (question.type === 'mctf') {
        // Convert from 'DSDS' to '1010'
        correctAnswer = '';
        for (let char of question.correctAnswer) {
          correctAnswer += char === 'D' ? '1' : '0';
        }
      }

      questionData.append('section', question.section);
      questionData.append('sectionContent', question.sectionContent);
      questionData.append('index', question.index.toString());
      questionData.append('type', question.type);
      questionData.append('correctAnswer', correctAnswer);
      questionData.append('reuseContent', question.reuseContent);
      questionData.append('questionImageUrl', question.questionImageUrl);
      questionData.append('explainImageUrl', question.explainImageUrl);
      questionData.append(
        'reuseContentImageUrl',
        question.reuseContentImageUrl
      );
      questionData.append('questionAImageUrl', question.questionAImageUrl);
      questionData.append('questionBImageUrl', question.questionBImageUrl);
      questionData.append('questionCImageUrl', question.questionCImageUrl);
      questionData.append('questionDImageUrl', question.questionDImageUrl);
      question.questionImage &&
        questionData.append('questionImage', question.questionImage);
      question.explainImage &&
        questionData.append('explainImage', question.explainImage);
      question.reuseContentImage &&
        questionData.append('reuseContentImage', question.reuseContentImage);
      question.questionAImage &&
        questionData.append('questionAImage', question.questionAImage);
      question.questionBImage &&
        questionData.append('questionBImage', question.questionBImage);
      question.questionCImage &&
        questionData.append('questionCImage', question.questionCImage);
      question.questionDImage &&
        questionData.append('questionDImage', question.questionDImage);

      questionData.append('examId', examId);
      questionData.append('question', question.question);
      questionData.append('explain', question.explain);
      questionData.append('questionA', question.questionA);
      questionData.append('questionB', question.questionB);
      questionData.append('questionC', question.questionC);
      questionData.append('questionD', question.questionD);
      return questionData;
    };

    const handleQuestions = async (
      questions: Array<Question>,
      examId: string,
      update = false
    ) => {
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        const questionData = processQuestionData(question, examId);
        try {
          update
            ? await updateQuestionById(question.questionId, questionData)
            : await addQuestion(questionData);

          // Update progress
          setProgress(Math.round(((i + 1) / totalQuestions) * 100));
        } catch (error) {
          console.error(
            `Failed to ${update ? 'update' : 'add'} question ${
              question.index
            }:`,
            error
          );
        }
      }
    };

    try {
      const examData = {
        ...examInput,
        questions: [],
        courseId,
        parentFolderId,
      };

      let newItemWithType;

      if (mode === 'edit' && examId) {
        const updatedItem = await updateExamById(examId, {
          ...examInput,
          questions: [],
        });
        newItemWithType = {
          ...updatedItem.data,
          type: 'exam',
          itemId: updatedItem.data.examId,
        };

        if (examInput.isEdittingQuestion) {
          await handleQuestions(examInput.questions, examId, true);
        }

        dispatch(updateCourseItem(newItemWithType));
      } else if (mode === 'create') {
        const newItem = await createNewExam(examData);
        newItemWithType = {
          ...newItem.data,
          type: 'exam',
          itemId: newItem.data.examId,
        };
        await handleQuestions(examInput.questions, newItem.data.examId);

        dispatch(addCourseItem(newItemWithType));
      }
    } catch (err) {
      console.error('Error saving exam:', err);
    } finally {
      setLoadingButton(false);
      closeDialog();
    }
  };

  const renderContent = () => {
    return (
      <>
        <div
          style={{
            display: activeTab === 0 ? 'block' : 'none',
          }}
        >
          <AnswerResultPage
            isDataAllFetched={isDataAllFetch}
            exam={exam || defaultExam}
            mode={mode}
            examInput={examInput}
            setExamInput={setExamInput}
            isTeacher={isTeacher}
            isNotOnDialog={isNotOnDialog}
            result={result}
            sortedResults={sortedResults}
            showResultByTime={showResult}
            handleSave={handleSave}
            loadingButton={loadingButton}
            isDataFilled={isDataFilled}
            progress={progress}
          />
        </div>
        <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
          <LeaderBoardPage
            exam={exam}
            isTeacher={isTeacher}
            isNotOnDialog={isNotOnDialog}
            sortedResults={sortedResults}
          />
        </div>
      </>
    );
  };

  const handleDownload = (pdfLink: string) => {
    // Download from google drive like this link https://drive.google.com/file/d/1DC59V3Jipw9dG87zhzeK2vZWKS7M_KCF/view?usp=drive_link
    const id = pdfLink.split('/')[5];
    const link = `https://drive.google.com/uc?export=download&id=${id}`;
    window.open(link, '_blank');
  };

  return (
    <Box
      sx={{
        maxWidth: '100%',
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
      }}
    >
      {/* Stepper */}
      {mode === 'view' ? (
        <>
          {/* Content */}
          <Box
            sx={{
              my: 0.5,
              position: 'relative',
              height: isNotOnDialog ? 'auto' : '90vh',
            }}
          >
            <Box display="flex" justifyContent="center">
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                centered
              >
                {fullSteps.map((step) => (
                  <Tab key={step.label} icon={step.icon} iconPosition="start" />
                ))}
              </Tabs>
            </Box>

            {renderContent()}
          </Box>
        </>
      ) : (
        <Box
          sx={{
            my: 4,
          }}
        >
          <AnswerResultPage
            isDataAllFetched={isDataAllFetch}
            exam={exam || defaultExam}
            mode={mode}
            examInput={examInput}
            setExamInput={setExamInput}
            isTeacher={isTeacher}
            isNotOnDialog={isNotOnDialog}
            result={null}
            onClosed={closeDialog}
            showResultByTime={showResult}
            handleSave={handleSave}
            loadingButton={loadingButton}
            isDataFilled={isDataFilled}
            progress={progress}
          />
        </Box>
      )}

      {!isNotOnDialog && (
        // x button on top right
        <>
          <IconButton
            onClick={closeDialog}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 100,
            }}
            color="primary"
          >
            <CloseIcon />
          </IconButton>
          {examInput.pdfLink && showResult && (
            <IconButton
              onClick={() => {
                handleDownload(examInput.pdfLink);
              }}
              sx={{
                position: 'absolute',
                top: 10,
                right: 50,
                zIndex: 100,
              }}
              color="primary"
            >
              <Download />
            </IconButton>
          )}
        </>
      )}

      {/* Save Button */}
      {/* {!isNotOnDialog && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: isMobile ? 0 : 2,
          }}
        >
          {(mode === 'edit' || mode === 'create') && (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loadingButton || isDataFilled}
            >
              {loadingButton ? (
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={progress}
                    size={24}
                    color="inherit"
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                    >
                      {`${progress}%`}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                'Save'
              )}
            </Button>
          )}
        </Box>
      )} */}

      <Box display={'none'}>
        <CountDownToResult
          exam={exam || null}
          timediff={timediff || 0}
          isTeacher={isTeacher}
          onExamFinished={setShowResult}
        />
      </Box>
    </Box>
  );
};

export default ExamTabs;
